import React, { Fragment, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Container from '../../../../core/components/atoms/Container';
import { BreadcrumbsItem } from '../../../../core/components/organism/Breadcrumbs';
import Material from '../../../../core/components/organism/Material';
import { markMaterialView } from '../../../../core/queries/materials';
import {
  getViewedFromResponse,
  materialViewTypeFromCategory,
} from '../../../../core/models/contentView';
import ContentFooter from '../../components/contentFooter';
import { useMaterial } from '../../hooks/useMaterials';
import { useMaterialStore } from '../../stores/materialStore';
import BrainLoadingWrapper from '../../../../core/components/molecules/BrainLoading';
import NoContent from '../../../../core/components/templates/NoContent';
import {
  getDisciplineName,
  getUniversitarioDisciplinePermission,
} from '../../../../core/utils/discipline';
import { getModuleLabel } from '../../utils/contentHelper';
import { IMaterial } from '../../../../core/models/disciplines';
import LockUniversitario from '../../../../core/components/organism/UniversitarioLock';
import { useAccess } from '../../../../core/accessControl/useAccess';
import FreemiumLockContent from '../../../../core/components/organism/FreemiumLockModal/LockContent';
import { useUserStore } from '../../../../modules/Auth/stores/authStore';
import {
  getUserPermissions,
  hasExactPermission,
} from '../../../../core/accessControl/utils';

const MaterialPage = React.memo(function MaterialPage() {
  const navigate = useNavigate();
  const { discipline, module, subject, slug } = useParams();
  const { isContentLocked } = useAccess();
  const { user } = useUserStore();

  const { mutate, isPending, isError } = useMaterial();
  const {
    materials,
    allMaterials,
    material,
    nextLink,
    previousLink,
    currentMaterialIndex,
    setMaterial,
    setMaterials,
    setNextAndPreviousMaterial,
  } = useMaterialStore();

  useEffect(() => {
    if (module) {
      mutate(module);
    }
  }, []);

  useEffect(() => {
    if(allMaterials.length > 0 && slug) {
      setMaterial(slug);
    }
  }, [allMaterials])

  useEffect(() => {
    if(allMaterials.length > 0 && slug) {
      const current = allMaterials.find((it: IMaterial) => it.slug === slug);

      // Como "opened" é toggle, só marca quando ainda não está visto — assim
      // reabrir um material já visto não o desmarca.
      if (current && !current.done) {
        const type = materialViewTypeFromCategory(current.file_category);

        markMaterialView(slug, type, 'free_browsing').then((res) => {
          const viewed = getViewedFromResponse(res);
          const materialIndex = materials.findIndex((it: IMaterial) => it.slug === slug);

          if (materialIndex !== -1) {
            materials[materialIndex].done = viewed;
            setMaterials(materials);
          }
        });
      }

      setMaterial(slug);
    }
  }, [slug])

  useEffect(() => {
    if(material) {
      dispatchSetContentAsDoneEvent();
    }
  }, [material])

  function dispatchSetContentAsDoneEvent() {
    const setContentAsDone = new CustomEvent('setContentAsDone', {
      detail: {
        contentId: material.id,
      },
    });
    
    window.dispatchEvent(setContentAsDone);
  }

  useEffect(() => {
    const baseLink = `/disciplinas/${discipline}/modulo/${module}/assunto/${subject}/material`;
    setNextAndPreviousMaterial(baseLink);
  }, [currentMaterialIndex]);

  if (isPending || !material) {
    return (
      <BrainLoadingWrapper />
    );
  }

  const nextMaterial = () => {
    if (nextLink) {
      navigate(nextLink);
    }
  };

  const previousMaterial = () => {
    if (previousLink) {
      navigate(previousLink);
    }
  };

  if(isError){
    const url = `/disciplinas/${discipline}/modulo/${module}/tipo/materiais`;

    return <NoContent url={url}/>
  }

  const breadcrumbItems: BreadcrumbsItem[] = [
    {
      label: discipline ? getDisciplineName(discipline) : '',
      href: `/disciplinas/${discipline}`,
    },
    {
      label: getModuleLabel(module || ''),
      href: `/disciplinas/${discipline}/modulo/${module}/tipo/materiais`,
    },
    {
      label: material?.title || '',
      href: '#',
    },
  ];

  const universitarioPermission = getUniversitarioDisciplinePermission(
    material?.ancestors?.area?.slug || '',
  );

  if (
    universitarioPermission &&
    !hasExactPermission(getUserPermissions(user), universitarioPermission)
  ) {
    return <LockUniversitario />
  }

  return (
    <Fragment>
      <Container>
        <div className='mb-16'>
          {isContentLocked(material) ? (
            <FreemiumLockContent className='max-w-[520px]' />
          ) : (
            <Material
              review={material.review}
              isFavorite={material.favorite}
              title={material.title}
              breadcrumb={breadcrumbItems}
              materialType={material.file_category}
              materialId={material.id}
              materialSlug={material.slug}
              file={material.uri}
            />
          )}
        </div>
      </Container>
      <ContentFooter
        contentModalInitialTab={2}
        contentModalSubtitle='Lista de materiais'
        previousLabel='Material anterior'
        nextLabel='Próximo material'
        nextContent={nextMaterial}
        previousContent={previousMaterial}
        currentIndex={currentMaterialIndex || 0}
        contentLength={allMaterials.length || 0}
        contentType="materiais"
      />
    </Fragment>
  );
});

export default MaterialPage;
